import { DialogDescription, DialogHeader, DialogTitle, cn } from '@ideoz/ui';
import { CheckCircle2, FileSearch, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ScopeIndexingRow } from '../useScopeIndexing';

interface ScopeAnalyzingViewProps {
  rows: ScopeIndexingRow[];
  /** Files indexed so far. */
  indexed: number;
  /** Files being indexed right now (drives the pulsing bar segments). */
  analyzing: number;
  total: number;
}

/**
 * The in-dialog "analyzing your files" gate: icon + value copy, a segmented
 * progress bar, and each file's real indexing status. Purely presentational —
 * `useScopeIndexing` owns the polling.
 */
export function ScopeAnalyzingView({
  rows,
  indexed,
  analyzing,
  total,
}: ScopeAnalyzingViewProps): JSX.Element {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.round((indexed / total) * 100) : 0;

  return (
    <>
      <DialogHeader className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <FileSearch className="h-7 w-7 text-primary" strokeWidth={1.75} />
        </span>
        <DialogTitle className="text-lg font-semibold text-gray-900">
          {t('analyzing.title')}
        </DialogTitle>
        <DialogDescription className="mx-auto max-w-md text-sm text-gray-500">
          {t('analyzing.description')}
        </DialogDescription>
      </DialogHeader>

      {/* Segmented bar — one segment per file, ordered by progress:
          indexed (solid) · analyzing (pulsing) · pending (idle). */}
      <div className="mt-1 flex items-center gap-3">
        <div className="flex flex-1 items-center gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-2 flex-1 rounded-full transition-colors duration-500',
                i < indexed
                  ? 'bg-primary'
                  : i < indexed + analyzing
                    ? 'bg-primary/40 animate-pulse'
                    : 'bg-gray-200 dark:bg-white/10'
              )}
            />
          ))}
        </div>
        <span className="w-9 shrink-0 text-end text-xs font-medium tabular-nums text-gray-500">
          {pct}%
        </span>
      </div>

      {/* Per-file status list — each file's real backend status. */}
      <ul className="divide-y divide-gray-100 dark:divide-white/10" data-testid="scope-analyzing">
        {rows.map((row, i) => (
          <li
            key={`${row.name}-${i}`}
            className="flex items-center gap-3 py-2.5"
            data-testid="scope-file-row"
            data-state={row.state}
          >
            <FileText
              className={cn(
                'h-4 w-4 shrink-0',
                row.state === 'pending' ? 'text-gray-300' : 'text-gray-400'
              )}
            />
            <span
              className={cn(
                'flex-1 truncate text-sm',
                row.state === 'pending' ? 'text-gray-400' : 'text-gray-800 dark:text-gray-100'
              )}
            >
              {row.name}
            </span>
            {row.state === 'indexed' && (
              <span className="flex items-center gap-1.5 text-sm text-success-600">
                <CheckCircle2 className="h-4 w-4" />
                {t('analyzing.indexed')}
              </span>
            )}
            {row.state === 'analyzing' && (
              <span className="flex items-center gap-1.5 text-sm text-primary">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('analyzing.analyzing')}
              </span>
            )}
            {row.state === 'pending' && (
              <span className="text-sm text-gray-400">{t('analyzing.pending')}</span>
            )}
          </li>
        ))}
      </ul>

      <p
        className="border-t border-gray-100 pt-3 text-center text-sm font-semibold text-gray-700 dark:border-white/10 dark:text-gray-200"
        data-testid="scope-analyzed-count"
      >
        {t('analyzing.count', { indexed, total })}
      </p>
    </>
  );
}

export default ScopeAnalyzingView;
